/**
 * The Popover content component.
 */
import * as React from "react"
import styled from "@oui/system"
import { Preference } from "../Tether/getPreference"
import Tether from "../Tether"
import useClickOutside from "../useClickOutside"
import useTheme from "../useTheme"
import { Space } from "@oui/system/spacing"
import {
  Components,
  interpolations as componentInterpolations
} from "@oui/system/components"
import {
  propsToMediaQueryStyles,
  ResponsiveProps
} from "@oui/system/breakpoints"
import * as Dimensions from "@oui/system/dimensions"
import FocusTrap from "../FocusTrap"
import { getAllActionables } from "../ActionableSet/utils"
import PopoverContext from "./PopoverContext"
import PopoverProvider from "./PopoverProvider"
import useMergeRefs from "../useMergeRefs"
import { omit } from "ramda"

type Appearance = keyof Components["Popover"]["appearances"]
type BreakpointAwareProps = ResponsiveProps<Dimensions.Props>
type DefaultProps = typeof defaultProps
type Offset = { top: Space; left: Space }
type Props = Readonly<
  {
    /**
     * The element used for positioning.
     */
    anchor: HTMLElement | null
    /**
     * Content to be rendered.
     */
    children: React.ReactElement
    /**
     * A function for hiding the element.
     */
    hide: () => void
    /**
     * A unique id for the element.
     */
    id: string
    /**
     * A boolean for controlling conditional rendering.
     */
    visible: boolean
  } & BreakpointAwareProps &
    DefaultProps
>
type InternalProps = Readonly<
  {
    id: string
    onKeyDown: React.KeyboardEventHandler
  } & Pick<React.HTMLAttributes<HTMLDivElement>, "children"> &
    BreakpointAwareProps
>

const defaultProps = {
  /**
   * The visual variant that determines the styling applied.
   * @default "default"
   */
  appearance: "default" as Appearance,
  /**
   * The amount of space between the Tooltip and anchor.
   * @default { top: "sm", left: "sm" }
   */
  offset: ({ top: "sm", left: "sm" } as any) as Offset,
  /**
   * A handler that is called when the ||ESC|| key is pressed. Returning `true`
   * will hide the `Dialog`.
   * @default () => true
   */
  onEscapeKeyDown: () => true,
  /**
   * A handler that is called when a click event happens outside of the dialog
   * element. Returning `true` will hide the `Popover`.
   * @default () => true
   */
  onClickOutside: () => true,
  /**
   * The preferred placement of the Tooltip.
   * @default "top"
   */
  placement: "top" as Preference
}

const { default: dimensionInterpolations } = Dimensions
const interpolations = {
  ...componentInterpolations.Popover,
  ...dimensionInterpolations
}

const Popover_ = ({
  id,
  forwardedRef: ref,
  ...rest
}: InternalProps & { forwardedRef: React.Ref<HTMLDivElement> }) => (
  <div id={id} ref={ref} {...omit(Object.keys(interpolations), rest)} />
)

const ForwardedPopover_ = (
  props: InternalProps,
  ref: React.Ref<HTMLDivElement>
) => <Popover_ {...props} forwardedRef={ref} />
ForwardedPopover_.displayName = "forwardRef(Popover)"
const ForwardedPopover = React.forwardRef(ForwardedPopover_)

const StyledPopover = styled(ForwardedPopover)(
  {},
  propsToMediaQueryStyles(Object.values(interpolations))
)

const Popover = ({
  anchor,
  hide,
  offset: userOffset,
  onEscapeKeyDown,
  onClickOutside,
  placement,
  visible,
  ...rest
}: Props) => {
  const { spacing } = useTheme()
  const offset = {
    top: spacing[userOffset.top],
    left: spacing[userOffset.left]
  }

  const localRef = React.createRef<HTMLDivElement>()
  const { stack } = React.useContext(PopoverContext)
  const [tabIndex, setTabIndex] = React.useState(-1)

  // autoFocus
  const autoFocus = (element: HTMLDivElement | null) => {
    if (!element || !autoFocus) return
    // set focus to highest tabIndex value
    const actionables = getAllActionables(element).sort((a, b) => {
      const idxA = parseFloat(a.getAttribute("tabIndex") ?? "0")
      const idxB = parseFloat(b.getAttribute("tabIndex") ?? "0")
      return idxB - idxA
    })

    const firstActionableElement = actionables[0] || element
    if (actionables.length === 0) setTabIndex(0)
    firstActionableElement.focus({ preventScroll: true })
  }

  const addToStack = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (!element) return
      stack?.current.push(element)
    },
    [stack]
  )

  const ref = useMergeRefs(localRef, autoFocus, addToStack)

  // hooks from running
  const handleClickOutside = React.useCallback<
    Parameters<typeof useClickOutside>["0"]["onClickOutside"]
  >(
    event => {
      if (!localRef.current || !event.target) return
      event.preventDefault()
      const idx = stack.current.indexOf(localRef.current)
      const content = stack.current.slice(idx)
      const hasClickInside = content.some(element =>
        element.contains(event.target as Node)
      )
      !hasClickInside && onClickOutside() && hide()
      return
    },
    [hide, localRef, onClickOutside, stack]
  )

  useClickOutside({ ref: localRef, onClickOutside: handleClickOutside })

  const handleKeyDown = React.useCallback<React.KeyboardEventHandler>(
    event => {
      if (["Escape", "Esc"].includes(event.key)) {
        event.preventDefault()
        event.stopPropagation()
        onEscapeKeyDown() && hide()
      }
    },
    [hide, onEscapeKeyDown]
  )

  const props = {
    ...rest,
    ref,
    onKeyDown: handleKeyDown,
    tabIndex
  }

  return (
    <Tether
      placement={placement}
      anchorRef={{ current: anchor }}
      offset={offset}
    >
      <FocusTrap>
        <StyledPopover {...props} />
      </FocusTrap>
    </Tether>
  )
}
Popover.defaultProps = defaultProps

const PopoverWrapper = (
  props: Omit<Props, keyof DefaultProps> & Partial<DefaultProps>
) => {
  return props.anchor && props.visible ? (
    <PopoverProvider>
      <Popover {...props} />
    </PopoverProvider>
  ) : null
}
Popover.defaultProps = defaultProps

export { Popover as _PropsComponent_ }
export default PopoverWrapper
